import type { PropsWithChildren } from "react";
import { useMemo } from "react";

import { useTranslate } from "@similarweb/i18n-provider";

import { NOT_AVAILABLE } from "app/filters/const";
import ManagementCard from "app/pages/sales-intelligence/modules/manage-integrations/components/cards-components/ManagementCard";
import ManagementCardListItem from "app/pages/sales-intelligence/modules/manage-integrations/components/cards-components/ManagementCardListItem";
import {
    StyledManagementCardContentList,
    StyledManagementCardFooterWrapper,
    StyledManagementCardTagWrapper,
} from "app/pages/sales-intelligence/modules/manage-integrations/components/cards-components/styles";
import {
    MANAGE_INTEGRATIONS_CARD_CONFIGURE,
    MANAGE_INTEGRATIONS_CARD_CONFIGURED,
    MANAGE_INTEGRATIONS_CARD_MANAGE,
    MY_INTEGRATIONS_SIGNALS_WEBHOOKS_EMPTY_TEXT,
    WEBHOOKS_LAST_DELIVERY,
    WEBHOOKS_SIGNALS_ACTIVE_SIGNALS,
    WEBHOOKS_SIGNALS_TITLE,
    WEBHOOKS_SIGNALS_TRACKED_DOMAINS,
} from "app/pages/sales-intelligence/modules/manage-integrations/constants/translations";
import manageIntegrationsTrackingService from "app/pages/sales-intelligence/modules/manage-integrations/services/tracking";
import { salesforceFormatDate } from "app/pages/sales-intelligence/modules/manage-integrations/utils/salesforceFormatDate";
import {
    goToSignalsWebhookGateway,
    manageSignalsWebhook,
} from "app/pages/sales-intelligence/modules/manage-integrations/utils/signalsIntegrationActions";
import { SIGNALS_API_DOCS_URL } from "app/pages/sales-intelligence/modules/management/sub-modules/constants";
import {
    MANAGEMENT_COMMON_DAYS,
    MANAGEMENT_COMMON_HOURS,
    MANAGEMENT_COMMON_MINUTES,
} from "app/pages/sales-intelligence/pages/management/translations/managementKeys";
import { formatNumberWithComa } from "app/pages/sales-intelligence/utils/leads";

import type { ISignalsApiData } from "../../../hooks/useSignalsApiData";
import openExternalLink from "../../../utils/openExternalLink";
import ManagementCardTextBody from "../../cards-components/ManagementCardTextBody";
import IntegrationButton from "app/pages/sales-intelligence/modules/manage-integrations/components/integration-button/IntegrationButton";

export const SignalsWebhookCard = ({
    data,
    signalsCount,
    subscribedDomainsCount,
    isConfigured,
    isEnabled,
    hasAccess,
    children,
}: PropsWithChildren<ISignalsApiData>): React.ReactElement => {
    const { translate } = useTranslate();

    const trackedDomainsText = subscribedDomainsCount ? formatNumberWithComa(subscribedDomainsCount) : NOT_AVAILABLE;
    const activeSignalsText = signalsCount ? formatNumberWithComa(signalsCount) : NOT_AVAILABLE;
    const lastDeliveryText = useMemo(() => {
        return data?.lastNotifiedEventDate
            ? salesforceFormatDate(
                  `${data?.lastNotifiedEventDate}Z`,
                  translate(MANAGEMENT_COMMON_MINUTES),
                  translate(MANAGEMENT_COMMON_HOURS),
                  translate(MANAGEMENT_COMMON_DAYS),
              )
            : NOT_AVAILABLE;
    }, [data]);

    const handleGuideClick = () => {
        manageIntegrationsTrackingService.trackSignalsWebhookGuideClick();
        openExternalLink(SIGNALS_API_DOCS_URL);
    };

    const handleManageClick = () => {
        manageSignalsWebhook({ isConfigured, data });
    };

    const cardFooter = (
        <StyledManagementCardFooterWrapper>
            <StyledManagementCardTagWrapper>{children}</StyledManagementCardTagWrapper>

            <IntegrationButton
                dataAutomation="signals-webhook-button"
                featureName="signalsWebhook"
                mainButtonType={isConfigured ? "outlined" : "primary"}
                hasAccess={hasAccess}
                isEnabled={isEnabled}
                onManageClick={handleManageClick}
                onUpgradeClick={goToSignalsWebhookGateway}
            >
                {translate(isConfigured ? MANAGE_INTEGRATIONS_CARD_MANAGE : MANAGE_INTEGRATIONS_CARD_CONFIGURE)}
            </IntegrationButton>
        </StyledManagementCardFooterWrapper>
    );

    const cardBody = isConfigured ? (
        <StyledManagementCardContentList>
            <ManagementCardListItem title={translate(WEBHOOKS_SIGNALS_TRACKED_DOMAINS)}>
                {trackedDomainsText}
            </ManagementCardListItem>
            <ManagementCardListItem title={translate(WEBHOOKS_SIGNALS_ACTIVE_SIGNALS)}>
                {activeSignalsText}
            </ManagementCardListItem>
            <ManagementCardListItem title={translate(WEBHOOKS_LAST_DELIVERY)}>
                {lastDeliveryText}
            </ManagementCardListItem>
        </StyledManagementCardContentList>
    ) : (
        <ManagementCardTextBody>{translate(MY_INTEGRATIONS_SIGNALS_WEBHOOKS_EMPTY_TEXT)}</ManagementCardTextBody>
    );

    return (
        <ManagementCard
            dataAutomation="signals-webhook-card"
            title={translate(WEBHOOKS_SIGNALS_TITLE)}
            chipText={isConfigured ? translate(MANAGE_INTEGRATIONS_CARD_CONFIGURED) : undefined}
            chipType="success"
            onHeaderActionClick={!isConfigured ? handleGuideClick : undefined}
            footer={cardFooter}
        >
            {cardBody}
        </ManagementCard>
    );
};
